import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { doc, getDoc, setDoc, deleteDoc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from 'firebase/functions';
import { functionsEurope } from '../firebase';
import { getCampaignSearchFields } from '../utils/campaignSearchUtils';
import { db } from '../firebase';
import { useUser } from '../userContext';
import Loading from "../common/DefaultLoading";
import Stats, { StatsSkeleton } from "./stats";
import CampaignBanner from '../dashboard/CampaignBanner';
import DeleteCampaign from "../dashboard/DeleteCampaign";
import AlertError from "./PermissionDenied";
import ManageLeaderboard from './ManageLeaderboard';
import { cacheHelpers } from '../utils/campaignCache';
import { duplicateCampaign } from '../services/campaignService';
import { fetchGameStats, fetchGameDailyStats, invalidateGameStats, getStatsCacheDuration, getPageViewCount } from '../services/gameAnalytics';
import { normalizeCampaignStatus, isCampaignShareable } from '../data/campaignStatus';
import { leaderboardService } from '../services/leaderboardService';
import ModalBasic from '../components/ModalBasic';
import { useCreditModal } from '../components/CreditModalContext';
import CopyLinkButton from '../components/CopyLinkButton';
import SlugEditor from '../components/SlugEditor';
import FreshnessIndicator from '../components/FreshnessIndicator';


import formatCompact from '../utils/formatCompact';
import { PencilSquareIcon, PlayIcon, DocumentDuplicateIcon, CheckCircleIcon, QrCodeIcon, ArrowTopRightOnSquareIcon, ShareIcon, CodeBracketIcon, ExclamationTriangleIcon, TrashIcon, ArrowDownTrayIcon, ChartBarIcon, CursorArrowRaysIcon, UserGroupIcon, ClockIcon, DevicePhoneMobileIcon, ComputerDesktopIcon, DeviceTabletIcon, LockClosedIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { BoltIcon } from '@heroicons/react/24/solid';
import { QRCodeCanvas } from 'qrcode.react';
import { AnimatePresence, motion } from 'framer-motion';
import { notifyError, notifySuccess } from '../utils/notify';
import { downloadCSV, sanitizeFilename } from '../utils/csvExport';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { getTemplateByCampaignId, addTemplate, removeTemplate } from '../services/templateService';
import { ShieldCheckIcon } from '@heroicons/react/24/solid';

// Cache for campaign data
const campaignCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Password generation utility for admin
const generateSecurePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let password = '';
  for (let i = 0; i < 8; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

// Hash password utility
const hashPassword = async (password) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Download QR code as PNG from a hidden canvas
const downloadQR = (url, filename) => {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  document.body.appendChild(container);
  const root = require('react-dom/client').createRoot(container);
  root.render(
    React.createElement(QRCodeCanvas, { value: url, size: 300, bgColor: '#ffffff', fgColor: '#2c3e50', level: 'M', includeMargin: true })
  );
  requestAnimationFrame(() => {
    const canvas = container.querySelector('canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
    root.unmount();
    document.body.removeChild(container);
  });
};

function CampaignSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      <div className="col-span-12 md:col-span-8">
        <div className="gch-card p-4">
          <div className="flex items-center justify-between">
            <div className="space-y-3 flex-1">
              <div className="h-4 rounded w-1/3 animate-pulse bg-white/10" />
              <div className="h-6 rounded w-1/2 animate-pulse bg-white/10" />
            </div>
            <div className="h-8 w-24 rounded-lg animate-pulse bg-white/10" />
          </div>
        </div>
        <StatsSkeleton />
      </div>
      <RightSideSkeleton />
    </div>
  );
}

function RightSideSkeleton() {
  return (
    <div className="col-span-12 md:col-span-4 space-y-6">
      <div className="gch-card p-4">
        <div className="h-4 rounded-lg animate-pulse w-1/2 mb-4 bg-white/10" />
        <div className="h-32 rounded-lg animate-pulse bg-white/10" />
      </div>
      <div className="gch-card p-4">
        <div className="h-10 rounded-lg animate-pulse bg-white/10" />
      </div>
    </div>
  );
}

export default function EditCampaign() {
  const params = useParams();
  const { user, businessProfile, campaigns, userProfile, canPublish, gameCredits, openUpgradeModal } = useUser();
  // Subscriptions retired: hardcoded "free" account values for any remaining gates.
  const isSubscribed = false;
  const navigate = useNavigate();
  const { openCreditModal } = useCreditModal();
  
  const [campaign, setCampaign] = useState(null);
  const campaignLoadedRef = useRef(false);
  const [formValues, setFormValues] = useState(null);
  const [adminMessage, setAdminMessage] = useState('');
  const [error, setError] = useState(null); 
  const [showAlert, setShowAlert] = useState(false);
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);
  const [showFirstPublishModal, setShowFirstPublishModal] = useState(false);
  const [firstPublishLinkCopied, setFirstPublishLinkCopied] = useState(false);
  const [firstPublishEmbedCopied, setFirstPublishEmbedCopied] = useState(false);
  // Confirmation modal for the inline "Ready to publish?" nudge button.
  // Without this, that button bypassed the CampaignBanner's confirm flow
  // and silently committed the free slot / credit. Now we always confirm
  // and show a loading state so the user knows something happened.
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [isPublishingFromNudge, setIsPublishingFromNudge] = useState(false);
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
  const [isSlugModalOpen, setIsSlugModalOpen] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [socialCopied, setSocialCopied] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showStatsSkeleton, setShowStatsSkeleton] = useState(true);
  
  // Duplicate game state
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);


  // Revert to draft modal
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [isReverting, setIsReverting] = useState(false);

  // SaaS v1: when the user returns from a successful credit-pack checkout
  // (URL carries `?credit_purchase=success&session_id=...`), show a one-shot
  // confirmation toast and clean the query string. Publishing is left as a
  // deliberate next click so the user always knows when a credit gets spent.
  const creditPurchaseToastRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const search = new URLSearchParams(window.location.search);
    const status = search.get('credit_purchase');
    if (status !== 'success' && status !== 'cancel') return;
    if (creditPurchaseToastRef.current) return;
    creditPurchaseToastRef.current = true;
    if (status === 'success') {
      notifySuccess('Credit added. Click Publish to ship your game.');
    } else {
      notifyError('Checkout cancelled. Your credits are unchanged.');
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('credit_purchase');
    url.searchParams.delete('session_id');
    window.history.replaceState({}, '', url.toString());
  }, []);


  const [statsUpdatedAt, setStatsUpdatedAt] = useState(null);
  const [insightsTab, setInsightsTab] = useState('devices');

  // Leaderboard totals (from ManageLeaderboard for Reset in Game Operations)
  const [leaderboardTotalEntries, setLeaderboardTotalEntries] = useState(0);
  const [leaderboardEmailCount, setLeaderboardEmailCount] = useState(0);
  // Page view count → "scan/click → play" conversion display.
  const [pageViewCount, setPageViewCount] = useState(0);
  const [leaderboardResetKey, setLeaderboardResetKey] = useState(0);
  const [showResetLeaderboardModal, setShowResetLeaderboardModal] = useState(false);
  const [resetLeaderboardModalOpen, setResetLeaderboardModalOpen] = useState(false);
  const [resetLeaderboardConfirmText, setResetLeaderboardConfirmText] = useState('');
  const [isResettingLeaderboard, setIsResettingLeaderboard] = useState(false);
  const RESET_LEADERBOARD_CONFIRM_WORD = 'RESET';

  // Export report state
  const [isExporting, setIsExporting] = useState(false);


  // Results share modal state
  const [isResultsShareModalOpen, setIsResultsShareModalOpen] = useState(false);
  const [resultsPlainPassword, setResultsPlainPassword] = useState('');
  const [resultsUrlCopied, setResultsUrlCopied] = useState(false);
  const [resultsPasswordCopied, setResultsPasswordCopied] = useState(false);
  const [isGeneratingPassword, setIsGeneratingPassword] = useState(false);

  // OPTIMIZATION: Resolve campaign from state or context first (must be before any code that uses effectiveCampaign)
  const campaignFromContext = useMemo(() => {
    return campaigns?.find(c => c.id === params.id);
  }, [campaigns, params.id]);
  const effectiveCampaign = campaign ?? campaignFromContext;

  const handleLeaderboardTotals = useCallback(({ totalEntries, emailCount }) => {
    setLeaderboardTotalEntries(totalEntries);
    setLeaderboardEmailCount(emailCount);
  }, []);


  const handleShareResults = useCallback(async () => {
    if (!effectiveCampaign?.id) return;

    if (effectiveCampaign.resultsPasswordHash) {
      // Password already exists - just show the modal (no plain password to display)
      setResultsPlainPassword('');
      setIsResultsShareModalOpen(true);
      return;
    }

    // Generate new password
    setIsGeneratingPassword(true);
    try {
      const plain = generateSecurePassword();
      const hash = await hashPassword(plain);
      const docRef = doc(db, 'campaign', effectiveCampaign.id);
      await setDoc(docRef, { resultsPasswordHash: hash }, { merge: true });
      setCampaign((prev) => ({ ...prev, resultsPasswordHash: hash }));
      setResultsPlainPassword(plain);
      setIsResultsShareModalOpen(true);
    } catch (err) {
      console.error('Error generating results password:', err);
      notifyError('Failed to generate password');
    } finally {
      setIsGeneratingPassword(false);
    }
  }, [effectiveCampaign]);

  const handleRegenerateResultsPassword = useCallback(async () => {
    if (!effectiveCampaign?.id) return;
    setIsGeneratingPassword(true);
    try {
      const plain = generateSecurePassword();
      const hash = await hashPassword(plain);
      const docRef = doc(db, 'campaign', effectiveCampaign.id);
      await setDoc(docRef, { resultsPasswordHash: hash }, { merge: true });
      setCampaign((prev) => ({ ...prev, resultsPasswordHash: hash }));
      setResultsPlainPassword(plain);
      notifySuccess('New password generated');
    } catch (err) {
      console.error('Error regenerating results password:', err);
      notifyError('Failed to regenerate password');
    } finally {
      setIsGeneratingPassword(false);
    }
  }, [effectiveCampaign]);

  const handleOpenResetLeaderboardModal = () => {
    setShowResetLeaderboardModal(true);
    setTimeout(() => setResetLeaderboardModalOpen(true), 100);
  };

  const handleCloseResetLeaderboardModal = () => {
    setResetLeaderboardModalOpen(false);
    setTimeout(() => {
      setShowResetLeaderboardModal(false);
      setResetLeaderboardConfirmText('');
    }, 300);
  };

  const handleResetLeaderboard = async () => {
    if (resetLeaderboardConfirmText !== RESET_LEADERBOARD_CONFIRM_WORD || !effectiveCampaign?.id) return;
    setIsResettingLeaderboard(true);
    try {
      await leaderboardService.resetLeaderboard(effectiveCampaign.id);
      setResetLeaderboardConfirmText('');
      handleCloseResetLeaderboardModal();
      setLeaderboardResetKey((k) => k + 1);
    } catch (error) {
      console.error('Error resetting leaderboard:', error);
    } finally {
      setIsResettingLeaderboard(false);
    }
  };

  // Game stats for status/mode derivation (totalPlays)
  // Phase 4: Campaign-level stats state (single fetch orchestrator)
  const [campaignStats, setCampaignStats] = useState(null); // aggregated lifetime stats
  const [campaignDailyStats, setCampaignDailyStats] = useState(null); // daily stats array
  const campaignStatsForceRef = useRef(false);

  const handleExportReport = useCallback(async () => {
    if (!effectiveCampaign?.id || isExporting) return;
    setIsExporting(true);
    try {
      const [stats, dailyStats] = await Promise.all([
        fetchGameStats(effectiveCampaign.id),
        fetchGameDailyStats(effectiveCampaign.id, 90)
      ]);
      const title = effectiveCampaign.options?.title || effectiveCampaign.internalName || 'Untitled';
      const rows = [
        ['Campaign Report'],
        ['Campaign Name', title],
        ['Status', normalizeCampaignStatus(effectiveCampaign.status)],
        ['Game Type', effectiveCampaign.gameType || '-'],
        [],
        ['Lifetime Metrics'],
        ['Total Plays', stats?.totalPlays ?? 0],
        ['Completions', stats?.totalCompletions ?? 0],
        ['Shares', stats?.totalShares ?? 0],
        ['CTA Clicks', stats?.totalCtas ?? 0],
        ['Leads', stats?.totalLeaderboardJoins ?? 0],
        ['Email Captures', stats?.totalEmailCaptures ?? 0],
        ['Completion Rate', `${stats?.completionRate ?? 0}%`],
        ['Share Rate', `${stats?.shareRate ?? 0}%`],
        ['CTA Rate', `${stats?.ctaRate ?? 0}%`],
        ['Avg Duration (sec)', stats?.avgDurationSeconds ?? 0],
        ['Desktop', stats?.devices?.desktop ?? 0],
        ['Mobile', stats?.devices?.mobile ?? 0],
        ['Tablet', stats?.devices?.tablet ?? 0],
        [],
        ['Daily Breakdown'],
        ['Date', 'Plays', 'Completions', 'Shares', 'CTAs', 'Leads', 'Email Captures'],
      ];
      if (Array.isArray(dailyStats)) {
        dailyStats.forEach(d => {
          rows.push([d.date, d.plays ?? 0, d.completions ?? 0, d.shares ?? 0, d.ctas ?? 0, d.leaderboardJoins ?? 0, d.emailCaptures ?? 0]);
        });
      }
      const filename = `${sanitizeFilename(title)}-report-${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(rows, filename);
      notifySuccess('Report exported');
    } catch (err) {
      console.error('Export report error:', err);
      notifyError('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  }, [effectiveCampaign, isExporting]);

  const campaignStatus = useMemo(() => normalizeCampaignStatus(effectiveCampaign?.status), [effectiveCampaign?.status]);

  // Subscriptions retired: every workspace gets the same analytics window.
  const analyticsLimit = 90;
  const [campaignRefreshKey, setCampaignRefreshKey] = useState(0);
  const [autoRefreshMs, setAutoRefreshMs] = useState(30000);

  useEffect(() => {
    const id = effectiveCampaign?.id;
    if (!id) return;
    let cancelled = false;

    const shouldForceRefresh = campaignStatsForceRef.current;
    campaignStatsForceRef.current = false;

    Promise.all([
      fetchGameStats(id, { forceRefresh: shouldForceRefresh }),
      fetchGameDailyStats(id, analyticsLimit, { forceRefresh: shouldForceRefresh }),
      // Page view count for scan/click → play conversion. One count read
      // per refresh, regardless of total view volume.
      getPageViewCount(id),
    ]).then(([stats, daily, views]) => {
      if (cancelled) return;
      setCampaignStats(stats);
      setCampaignDailyStats(daily);
      setPageViewCount(views || 0);
      setStatsUpdatedAt(Date.now());
      setAutoRefreshMs(getStatsCacheDuration(stats?.totalPlays ?? 0) || 30000);
    }).catch(err => {
      console.error('Error fetching campaign stats:', err);
    });
    return () => { cancelled = true; };
  }, [effectiveCampaign?.id, analyticsLimit, campaignRefreshKey]);

  // Auto-refresh interval
  useEffect(() => {
    if (!effectiveCampaign?.id) return;
    const MIN_REFRESH = 10000;
    const delay = Math.max(autoRefreshMs, MIN_REFRESH);
    const interval = setInterval(() => {
      if (document.visibilityState !== 'hidden') {
        setCampaignRefreshKey(k => k + 1);
      }
    }, delay);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') setCampaignRefreshKey(k => k + 1);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [effectiveCampaign?.id, autoRefreshMs]);

  const handleStatsForceRefresh = useCallback(() => {
    campaignStatsForceRef.current = true;
    setCampaignRefreshKey(k => k + 1);
  }, []);

  // Derive sidebar stats from fetched data (replaces onStatsForSidebar callback)
  const derivedSidebarStats = useMemo(() => {
    if (!campaignStats) return null;
    const s = campaignStats;
    const completionRate = s.totalPlays > 0 ? Math.round((s.totalCompletions / s.totalPlays) * 100) : 0;
    const shareRate = s.totalCompletions > 0 ? Math.round(((s.totalShares || 0) / s.totalCompletions) * 100) : 0;
    const ctaRate = s.totalCompletions > 0 ? Math.round((s.totalCtas / s.totalCompletions) * 100) : 0;

    // Aggregate traffic sources from daily stats.
    // Prefer full referrerCounts/utmSourceCounts maps (per-play counts); fall back to topReferrers
    // string arrays (+1 per day) for older daily-stats docs that lack the count maps.
    const referrerCounts = {};
    const utmCounts = {};
    (campaignDailyStats || []).forEach(entry => {
      if (entry.referrerCounts && typeof entry.referrerCounts === 'object') {
        Object.entries(entry.referrerCounts).forEach(([k, v]) => {
          if (k) referrerCounts[k] = (referrerCounts[k] || 0) + (Number(v) || 0);
        });
      } else {
        (entry.topReferrers || []).forEach(r => {
          const key = typeof r === 'object' && r !== null ? (r.key || '') : r;
          if (key) referrerCounts[key] = (referrerCounts[key] || 0) + 1;
        });
      }
      if (entry.utmSourceCounts && typeof entry.utmSourceCounts === 'object') {
        Object.entries(entry.utmSourceCounts).forEach(([k, v]) => {
          if (k) utmCounts[k] = (utmCounts[k] || 0) + (Number(v) || 0);
        });
      } else {
        (entry.topUtmSources || []).forEach(u => {
          const key = typeof u === 'object' && u !== null ? (u.key || '') : u;
          if (key) utmCounts[key] = (utmCounts[key] || 0) + 1;
        });
      }
    });
    const isSelfReferrer = (r) => /^(direct|localhost|.*smilesflow\..*)$/i.test(r);
    const topReferrers = Object.entries(referrerCounts)
      .filter(([name]) => !isSelfReferrer(name))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));
    const topUtmSources = Object.entries(utmCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));

    // Sum unique players from daily stats (exclude today - rollup runs at 2am UTC)
    const todayKey = new Date().toISOString().split('T')[0];
    const uniquePlayers = (campaignDailyStats || [])
      .filter(d => d.date !== todayKey)
      .reduce((sum, d) => sum + (d.uniquePlayers || 0), 0);

    return {
      totalPlays: s.totalPlays,
      totalCompletions: s.totalCompletions,
      totalCtaClicks: s.totalCtas,
      totalShares: s.totalShares || 0,
      totalLeaderboardJoins: s.totalLeaderboardJoins || 0,
      totalEmailCaptures: s.totalEmailCaptures || 0,
      avgDurationSeconds: s.avgDurationSeconds || 0,
      devices: s.devices || { mobile: 0, desktop: 0, tablet: 0 },
      completionRate,
      shareRate,
      ctaRate,
      uniquePlayers,
      topReferrers,
      topUtmSources,
    };
  }, [campaignStats, campaignDailyStats]);

  const isShareable = isCampaignShareable(effectiveCampaign?.status);
  const runMode = campaignStatus === 'published' || campaignStatus === 'archived';
  const completedMode = campaignStatus === 'archived';
  const draftMode = campaignStatus === 'draft';
  const shareImage = effectiveCampaign?.options?.sharingPhoto || effectiveCampaign?.options?.coverPhoto || '/og-game-default.png';
  const shareTitle = effectiveCampaign?.options?.title || 'Your Campaign';
  const slugSuggestionSource = effectiveCampaign?.options?.title
    || effectiveCampaign?.internalName
    || effectiveCampaign?.name
    || effectiveCampaign?.gameTypeDisplayValue
    || effectiveCampaign?.gameType
    || 'game';
  const embedCode = effectiveCampaign?.id ? `<!-- SmilesFlow Embed -->\n<div data-sf-embed>\n  <iframe src="${window.location.origin}/p/${effectiveCampaign.id}?utm_source=embed" style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;" title="Interactive Game by SmilesFlow" loading="lazy" allow="web-share; fullscreen; autoplay" sandbox="allow-scripts allow-same-origin allow-forms allow-popups"></iframe>\n</div>\n<style>\n  [data-sf-embed]{position:relative;width:100%;max-width:420px;aspect-ratio:9/16;max-height:70vh;margin:0 auto;border-radius:16px;overflow:hidden;box-shadow:0 10px 30px rgba(0,0,0,0.15);background:#1a2530}\n  @media(min-width:768px){[data-sf-embed]{max-width:900px;aspect-ratio:16/9;max-height:none}}\n</style>` : '';


  const getCampaignCreatedDate = (createdAt) => {
    if (!createdAt) return null;
    if (createdAt?.toDate) return createdAt.toDate();
    if (typeof createdAt?.seconds === 'number') {
      return new Date(createdAt.seconds * 1000);
    }
    const date = new Date(createdAt);
    return isNaN(date.getTime()) ? null : date;
  };

  const campaignCreatedDate = getCampaignCreatedDate(effectiveCampaign?.createdAt);
  const campaignCreatedLabel = campaignCreatedDate
    ? campaignCreatedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : ' -';
  const campaignMeta = effectiveCampaign ? (
    <div className="pb-3 mb-3">
      <div className="grid grid-cols-2 gap-2 text-xs gch-text-secondary">
        <div>
          <p className="text-[11px] uppercase tracking-wide gch-text-muted">Created</p>
          <p className="font-semibold gch-text-primary">{campaignCreatedLabel}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide gch-text-muted">Game type</p>
          <p className="font-semibold gch-text-primary">{effectiveCampaign.gameTypeDisplayValue || effectiveCampaign.gameType || ' -'}</p>
        </div>
      </div>
    </div>
  ) : null;

  useEffect(() => {
    if (!user || !businessProfile) return;

    async function fetchCampaign() {
      // Clear stale errors on every re-run (e.g. after workspace switch)
      setError(null);

      // When we have context data, don't flash loading/skeleton  - set state and show content immediately
      if (params.id && campaignFromContext) {
        // Use functional updates to avoid overwriting fresher local state
        // (e.g. after a publish transition updates status before context catches up)
        setCampaign(prev => prev ?? campaignFromContext);
        setFormValues(prev => prev ?? campaignFromContext);
        setIsLoading(false);
        setShowStatsSkeleton(false);
        campaignLoadedRef.current = true;
        return;
      }

      // Only show skeleton/loading if we don't already have campaign data
      // (prevents blank flash when campaignFromContext briefly becomes undefined during parent refresh)
      if (campaignLoadedRef.current) return;
      setIsLoading(true);
      setShowStatsSkeleton(true);
      const minDelay = new Promise(resolve => setTimeout(resolve, 200));

      try {
        if (params.id) {
          // Check local cache
          const cacheKey = `campaign_${params.id}`;
          const cached = campaignCache.get(cacheKey);
          if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
            console.log('Using local campaign cache');
            setCampaign(cached.data);
            setFormValues(cached.data);
            await minDelay;
            setIsLoading(false);
            setShowStatsSkeleton(false);
            campaignLoadedRef.current = true;
            return;
          }

          // Fallback to Firestore
          console.log('Fetching campaign from Firestore');
          const docRef = doc(db, "campaign", params.id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const campaignData = docSnap.data();
            // Check access: campaign must belong to the current workspace OR a child workspace of the user's agency
            const isDirectMatch = campaignData.companyId === businessProfile.id;
            const isChildOfAgency = businessProfile.parentCompany
              ? campaignData.companyId === businessProfile.parentCompany
              : campaignData.companyId !== businessProfile.id; // not direct - check below
            if (!isDirectMatch) {
              // If current workspace is the agency, check if campaign is in one of its child workspaces
              // If current workspace is a child, campaign must match this child
              const campaignWsRef = doc(db, 'workspace', campaignData.companyId);
              const campaignWsSnap = await getDoc(campaignWsRef);
              const campaignWsData = campaignWsSnap.exists() ? campaignWsSnap.data() : null;
              const userAgencyId = businessProfile.parentCompany || businessProfile.id;
              const belongsToAgency = campaignWsData?.parentCompany === userAgencyId || campaignData.companyId === userAgencyId;
              if (!belongsToAgency) {
                setError("You don't have access to this game. It belongs to a different account.");
                return;
              }
            }
            const campaignWithId = { ...campaignData, id: docSnap.id };

            // Cache the data
            campaignCache.set(cacheKey, { data: campaignWithId, timestamp: Date.now() });

            setCampaign(campaignWithId);
            setFormValues(campaignWithId);
            campaignLoadedRef.current = true;
          } else {
            setError("Campaign not found.");
          }
        }
      } catch (error) {
        setError("Error fetching campaign.");
        console.error(error);
      } finally {
        await minDelay;
        setIsLoading(false);
        setShowStatsSkeleton(false);
      }
    }

    fetchCampaign();
  }, [params.id, user, businessProfile, campaignFromContext]);

  // OPTIMIZATION: Only setup real-time listener if we don't have fresh data from context
  useEffect(() => {
    if (!params.id || campaignFromContext) return; // Skip if we have context data

    const docRef = doc(db, "campaign", params.id);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const campaignData = docSnap.data();
        const campaignWithId = { ...campaignData, id: docSnap.id };
        setCampaign((prevCampaign) => ({
          ...prevCampaign,
          ...campaignWithId,
        }));

        // Update cache
        const cacheKey = `campaign_${params.id}`;
        campaignCache.set(cacheKey, { data: campaignWithId, timestamp: Date.now() });
      }
    });

    return () => unsubscribe();
  }, [params.id, campaignFromContext]);

  const handleUpdateCampaignStatus = useCallback(async (newStatus) => {
    const invalidateCaches = (updateData) => {
      const cacheKey = `campaign_${params.id}`;
      const cached = campaignCache.get(cacheKey);
      if (cached) {
        campaignCache.set(cacheKey, { data: { ...cached.data, ...updateData }, timestamp: Date.now() });
      }
      cacheHelpers.invalidateCampaign(params.id);
      cacheHelpers.invalidateAnalyticsLocalStorage(params.id);
      invalidateGameStats(params.id);
      const overallStatsCacheKey = 'overall_stats_cache';
      localStorage.removeItem(`${overallStatsCacheKey}_${businessProfile?.id}`);
      localStorage.removeItem(`campaign_card_stats_cache_${params.id}`);
    };

    const syncLeadStatus = async (status) => {
      try {
        const { syncLeadStatusFromCampaign } = await import('../utils/leadCampaignSync');
        const leadStatus = status === 'published' ? 'live' : status === 'archived' ? 'completed' : status;
        await syncLeadStatusFromCampaign(params.id, leadStatus, user?.email);
      } catch (syncError) {
        console.error('Error syncing lead status:', syncError);
      }
    };

    if (newStatus === 'published') {
      const currentStatus = effectiveCampaign?.status;
      const normalizedCurrent = normalizeCampaignStatus(currentStatus);

      if (normalizedCurrent === 'paused' || effectiveCampaign?.systemPaused) {
        // Resuming requires publish access - route through callable for server-side check.
        if (!canPublish) {
          openCreditModal({ campaignId: params.id });
          return;
        }
        try {
          const publishGame = httpsCallable(functionsEurope, 'publishGame');
          await publishGame({ campaignId: params.id });
          const updateData = { status: 'published', systemPaused: false, resumedAt: new Date().toISOString() };
          setCampaign((prev) => ({ ...(prev ?? campaignFromContext), ...updateData }));
          await syncLeadStatus('published');
          invalidateCaches(updateData);
          notifySuccess('Campaign resumed!');
        } catch (error) {
          if (error?.code === 'functions/resource-exhausted') {
            openCreditModal({ campaignId: params.id });
          } else {
            console.error("Error resuming campaign:", error);
            setError("Error resuming campaign. Please try again later.");
          }
        }
        return;
      }

      // Route through publishGame callable  - enforces credit check server-side.
      // The callable owns publishedAt (set on first publish only) so we don't
      // overwrite it locally on a re-publish; status is the only optimistic flip.
      try {
        const publishGame = httpsCallable(functionsEurope, 'publishGame');
        await publishGame({ campaignId: params.id });
        const updateData = { status: 'published' };
        setCampaign((prev) => ({ ...(prev ?? campaignFromContext), ...updateData }));
        await syncLeadStatus('published');
        invalidateCaches(updateData);
        // First-publish celebration: fires once per user (localStorage flag).
        // Shows the share kit (link / QR / embed) inline so the user knows
        // exactly what to do with their new live game.
        try {
          const seen = localStorage.getItem('sf_first_publish_seen') === '1';
          if (!seen) {
            setShowFirstPublishModal(true);
          } else {
            notifySuccess('Campaign published!');
          }
        } catch {
          notifySuccess('Campaign published!');
        }
      } catch (error) {
        if (error?.code === 'functions/resource-exhausted') {
          openCreditModal({ campaignId: params.id });
        } else {
          console.error("Error publishing campaign:", error);
          setError("Error publishing campaign. Please try again later.");
        }
      }
      return;
    }

    // Draft / archived  - direct Firestore write (no credit consumed)
    const docRef = doc(db, "campaign", params.id);
    try {
      const campaignSnapshot = await getDoc(docRef);
      if (campaignSnapshot.exists()) {
        const updateData = { status: newStatus };
        if (newStatus === 'archived') {
          updateData.archivedAt = new Date().toISOString();
        }
        await setDoc(docRef, updateData, { merge: true });
        setCampaign((prev) => ({ ...(prev ?? campaignFromContext), ...updateData }));
        await syncLeadStatus(newStatus);
        invalidateCaches(updateData);
        const statusLabels = { draft: 'Reverted to draft', archived: 'Campaign completed', paused: 'Campaign paused' };
        notifySuccess(statusLabels[newStatus] || 'Campaign updated');
      }
    } catch (error) {
      console.error("Error updating campaign status:", error);
      setError("Error updating campaign status. Please try again later.");
    }
  }, [params.id, businessProfile?.id, user?.email, effectiveCampaign, canPublish, openUpgradeModal]);

  // Check if current user is admin
  const isAdmin = userProfile?.role === 'admin';
  const creditUiEnabled = !isSubscribed;

  // Admin template state
  const [templateData, setTemplateData] = useState(null); // existing template or null
  const [templateChecked, setTemplateChecked] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', category: 'marketing', useCase: '', imageUrl: '', showOnLanding: false });
  const [templateImageFile, setTemplateImageFile] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [removingTemplate, setRemovingTemplate] = useState(false);
  const [adminActionLoading, setAdminActionLoading] = useState(null);

  // Check if campaign is already a template (admin only)
  useEffect(() => {
    if (!isAdmin || !effectiveCampaign?.id || templateChecked) return;
    getTemplateByCampaignId(effectiveCampaign.id).then(t => {
      setTemplateData(t);
      setTemplateChecked(true);
    });
  }, [isAdmin, effectiveCampaign?.id, templateChecked]);

  const handleAddTemplate = async () => {
    if (savingTemplate) return;
    setSavingTemplate(true);
    try {
      let imageUrl = templateForm.imageUrl;
      // Upload image if selected
      if (templateImageFile) {
        const storageRef = ref(storage, `public/templates/${Date.now()}_${templateImageFile.name}`);
        await uploadBytes(storageRef, templateImageFile);
        imageUrl = await getDownloadURL(storageRef);
      }
      const result = await addTemplate({
        ...templateForm,
        imageUrl,
        campaignId: effectiveCampaign.id,
        gameType: effectiveCampaign.gameType || effectiveCampaign.options?.gameType || '',
        createdBy: user?.uid || '',
      });
      setTemplateData(result);
      setShowTemplateModal(false);
      notifySuccess('Added to template library');
    } catch (error) {
      notifyError(error.message || 'Failed to add template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleRemoveTemplate = async () => {
    if (!templateData?.id || removingTemplate) return;
    setRemovingTemplate(true);
    try {
      await removeTemplate(templateData.id);
      setTemplateData(null);
      notifySuccess('Removed from template library');
    } catch (error) {
      notifyError('Failed to remove template');
    } finally {
      setRemovingTemplate(false);
    }
  };

  const handleAdminAction = async (action) => {
    if (adminActionLoading) return;
    const labels = { unpublish: 'Force unpublish', complete: 'Force complete', refundCredit: 'Refund credit' };
    if (!window.confirm(`${labels[action]}? This action cannot be undone.`)) return;
    setAdminActionLoading(action);
    try {
      const fn = httpsCallable(functionsEurope, 'adminCampaignAction');
      await fn({ campaignId: effectiveCampaign.id, action });
      notifySuccess(`${labels[action]} successful`);
      // Reload page to reflect changes
      window.location.reload();
    } catch (error) {
      notifyError(error.message || `Failed: ${labels[action]}`);
    } finally {
      setAdminActionLoading(null);
    }
  };

  // Handle duplicate game
  const handleDuplicate = async () => {
    setIsDuplicating(true);
    try {
      const result = await duplicateCampaign(effectiveCampaign.id, businessProfile.id);
      setDuplicateModalOpen(false);
      setShowDuplicateModal(false);
      notifySuccess('Campaign duplicated!');
      navigate(`/studio/campaign/${result.id}`);
    } catch (error) {
      console.error('Error duplicating campaign:', error);
      setError('Failed to duplicate campaign. Please try again.');
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleOpenDuplicateModal = () => {
    setShowDuplicateModal(true);
    setTimeout(() => setDuplicateModalOpen(true), 100);
  };

  const handleCloseDuplicateModal = () => {
    setDuplicateModalOpen(false);
    setTimeout(() => setShowDuplicateModal(false), 300);
  };


  const handleNativeShare = useCallback(async () => {
    const token = effectiveCampaign?.slug || effectiveCampaign?.id;
    if (!token) return;
    const url = `${window.location.origin}/p/${token}?utm_source=share`;
    const title = effectiveCampaign?.options?.title || 'Play this game';
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (e) {
        if (e?.name !== 'AbortError') console.error('Native share failed:', e);
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        notifySuccess('Link copied!');
      } catch (e) { console.error(e); }
    }
  }, [effectiveCampaign?.slug, effectiveCampaign?.id, effectiveCampaign?.options?.title]);

  const handleRevertConfirm = useCallback(async () => {
    if (!params.id || isReverting) return;
    setIsReverting(true);
    try {
      await handleUpdateCampaignStatus('draft');
      setShowRevertModal(false);
    } catch (err) {
      console.error('Failed to revert campaign:', err);
    } finally {
      setIsReverting(false);
    }
  }, [params.id, isReverting, handleUpdateCampaignStatus]);

  // Handle internal name save
  const handleInternalNameSave = useCallback(async (newInternalName) => {
    const docRef = doc(db, "campaign", params.id);
    
    try {
      const updateData = {
        internalName: newInternalName || null, // null removes the field if empty
        ...getCampaignSearchFields({
          internalName: newInternalName || null,
          options: effectiveCampaign?.options,
        }),
      };
      
      await setDoc(docRef, updateData, { merge: true });
      
      setCampaign((prevCampaign) => ({
        ...prevCampaign,
        internalName: newInternalName || null
      }));

      // Update cache
      const cacheKey = `campaign_${params.id}`;
      const cached = campaignCache.get(cacheKey);
      if (cached) {
        campaignCache.set(cacheKey, { 
          data: { ...cached.data, internalName: newInternalName || null }, 
          timestamp: Date.now() 
        });
      }
      
      console.log('✅ Internal name updated:', newInternalName || '(cleared)');
    } catch (error) {
      console.error("Error updating internal name:", error);
      throw error; // Re-throw so the component can handle it
    }
  }, [params.id, effectiveCampaign?.options]);

  // Only wait for user context (ignore loading to avoid brief skeleton flash)
  if (!businessProfile) {
    return <CampaignSkeleton />;
  }

  if (error) {
    return <AlertError text={error} />;
  }

  if (!effectiveCampaign || !effectiveCampaign.id) {
    return <CampaignSkeleton />;
  }

  // When showing from context, never show stats skeleton (avoids flash)
  const useStatsSkeleton = showStatsSkeleton && !campaignFromContext;

  return (
    <>

      <div className="gch-content-width gch-page pb-24">
          <CampaignBanner
            title={effectiveCampaign.options?.title || effectiveCampaign.internalName || 'Untitled Campaign'}
            subtitle={null}
            gameType={effectiveCampaign.gameType}
            internalName={effectiveCampaign.internalName}
            onInternalNameSave={handleInternalNameSave}
            status={effectiveCampaign.status}
            onStatusChange={handleUpdateCampaignStatus}
            gameCredits={gameCredits}
            isStudioSubscriber={isSubscribed}
            onNoCreditsClick={() => openCreditModal({ campaignId: params.id })}
            hidePublishButton={creditUiEnabled && gameCredits === 0 && effectiveCampaign.status === 'draft'}
          />

          {/* SaaS v1: free-or-paid publish nudge. The free path uses the
              workspace's single free slot (badge added). The paid path opens
              the credit-pack modal. The decision is purely client-side; the
              server enforces both. */}
          {creditUiEnabled && gameCredits === 0 && effectiveCampaign.status === 'draft' && (() => {
            const freeSlotCampaign = campaigns?.find(c => c.publishedFree === true && (c.status === 'published' || c.systemPaused === true));
            const hasFreeSlot = !freeSlotCampaign || freeSlotCampaign.id === effectiveCampaign.id;
            return (
              <div className="gch-card mt-4 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border border-[var(--admin-accent)]/30 bg-[var(--admin-accent)]/[0.06]">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <BoltIcon className="w-5 h-5 text-[var(--admin-accent)] shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold gch-text-primary">Ready to publish?</p>
                    <p className="text-xs gch-text-secondary mt-0.5">
                      Get a share link, QR code, and live leaderboard for your campaign.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => hasFreeSlot ? setShowPublishConfirm(true) : openCreditModal({ campaignId: params.id })}
                  disabled={isPublishingFromNudge}
                  className="btn-admin-primary btn-admin-md inline-flex items-center justify-center gap-2 shrink-0 w-full sm:w-auto disabled:opacity-60"
                >
                  {isPublishingFromNudge ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                      </svg>
                      Publishing…
                    </>
                  ) : (
                    <>
                      <BoltIcon className="w-4 h-4" />
                      Publish
                    </>
                  )}
                </button>
              </div>
            );
          })()}
      <AnimatePresence mode="wait">
        <motion.div
          key={useStatsSkeleton ? 'loading' : 'loaded'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >

          {useStatsSkeleton ? (
            <div className="mt-6">
              <StatsSkeleton />
            </div>
          ) : draftMode ? (
            <>
              <div className="grid grid-cols-12 gap-6 mt-6">
                <div className="col-span-12 lg:col-span-8 space-y-6">
                  <div className="gch-card p-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <Link
                      to={`/editor/${effectiveCampaign.id}`}
                      className="flex-1 btn-admin-primary btn-admin-md inline-flex items-center justify-center gap-2"
                    >
                      <PencilSquareIcon className="w-4 h-4" /> Keep editing
                    </Link>
                    <a
                      href={`/p/${effectiveCampaign.id}?preview=true`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 btn-admin-outline btn-admin-md inline-flex items-center justify-center gap-2"
                    >
                      <PlayIcon className="w-4 h-4" /> Test the game
                    </a>
                  </div>

                  {/* What you'll unlock - concrete previews of what publishing gives them */}
                  <div className="gch-card p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <LockClosedIcon className="w-4 h-4 text-[var(--admin-accent)]" />
                      <h3 className="text-sm font-semibold gch-text-primary">What publishing unlocks</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* Analytics & insights - wider tile, top of grid */}
                      <div className="sm:col-span-2 rounded-lg border border-dashed border-[var(--admin-border)] p-4 opacity-80">
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <div className="flex items-center gap-2">
                            <ChartBarIcon className="w-4 h-4 gch-text-secondary" />
                            <p className="text-xs font-semibold gch-text-primary">Analytics & insights</p>
                          </div>
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/10 gch-text-muted leading-none">Example</span>
                        </div>
                        <div className="grid grid-cols-4 gap-2 mb-2">
                          {[
                            { label: 'Plays', value: '1,284' },
                            { label: 'Completions', value: '742' },
                            { label: 'CTA clicks', value: '189' },
                            { label: 'Emails', value: '64' },
                          ].map(({ label, value }) => (
                            <div key={label} className="p-2 rounded bg-white/5 text-center">
                              <p className="text-sm font-bold tabular-nums gch-text-secondary">{value}</p>
                              <p className="text-[10px] uppercase tracking-wide gch-text-muted mt-0.5">{label}</p>
                            </div>
                          ))}
                        </div>
                        <p className="text-[11px] gch-text-muted leading-relaxed">Example numbers. Yours appear here once you publish, with conversion funnels, device breakdowns, and traffic sources in real time.</p>
                      </div>

                      {/* Share link & QR */}
                      <div className="rounded-lg border border-dashed border-[var(--admin-border)] p-4 opacity-80">
                        <div className="flex items-center gap-2 mb-2.5">
                          <ArrowTopRightOnSquareIcon className="w-4 h-4 gch-text-secondary" />
                          <p className="text-xs font-semibold gch-text-primary">Share link & QR code</p>
                        </div>
                        <div className="flex items-center gap-2 p-2 rounded-md bg-white/5 mb-2 min-w-0">
                          <p className="text-xs gch-text-muted font-mono truncate">smilesflow.com/p/{effectiveCampaign.slug || effectiveCampaign.id?.slice(0, 12)}</p>
                        </div>
                        <p className="text-[11px] gch-text-muted leading-relaxed">Branded URL plus a downloadable QR code for posters, emails, and events.</p>
                      </div>

                      {/* Lead capture */}
                      <div className="rounded-lg border border-dashed border-[var(--admin-border)] p-4 opacity-80">
                        <div className="flex items-center gap-2 mb-2.5">
                          <EnvelopeIcon className="w-4 h-4 gch-text-secondary" />
                          <p className="text-xs font-semibold gch-text-primary">Lead capture</p>
                        </div>
                        <div className="space-y-1 mb-2">
                          <div className="flex items-center justify-between p-1.5 rounded bg-white/5">
                            <span className="text-xs gch-text-muted truncate">sarah@example.com</span>
                            <span className="text-[10px] gch-text-muted shrink-0 ml-2">2,180</span>
                          </div>
                          <div className="flex items-center justify-between p-1.5 rounded bg-white/5">
                            <span className="text-xs gch-text-muted truncate">mike@example.com</span>
                            <span className="text-[10px] gch-text-muted shrink-0 ml-2">1,940</span>
                          </div>
                        </div>
                        <p className="text-[11px] gch-text-muted leading-relaxed">Collect player emails through the leaderboard sign-up.</p>
                      </div>

                      {/* Live leaderboard */}
                      <div className="rounded-lg border border-dashed border-[var(--admin-border)] p-4 opacity-80">
                        <div className="flex items-center gap-2 mb-2.5">
                          <ChartBarIcon className="w-4 h-4 gch-text-secondary" />
                          <p className="text-xs font-semibold gch-text-primary">Live leaderboard</p>
                        </div>
                        <div className="space-y-1 mb-2">
                          <div className="flex items-center justify-between p-1.5 rounded bg-white/5">
                            <span className="text-xs gch-text-muted">🥇 Sarah</span>
                            <span className="text-[10px] gch-text-muted tabular-nums">2,180</span>
                          </div>
                          <div className="flex items-center justify-between p-1.5 rounded bg-white/5">
                            <span className="text-xs gch-text-muted">🥈 Mike</span>
                            <span className="text-[10px] gch-text-muted tabular-nums">1,940</span>
                          </div>
                        </div>
                        <p className="text-[11px] gch-text-muted leading-relaxed">Top scores update in real time as people play.</p>
                      </div>

                      {/* Embed */}
                      <div className="rounded-lg border border-dashed border-[var(--admin-border)] p-4 opacity-80">
                        <div className="flex items-center gap-2 mb-2.5">
                          <CodeBracketIcon className="w-4 h-4 gch-text-secondary" />
                          <p className="text-xs font-semibold gch-text-primary">Embed on your site</p>
                        </div>
                        <div className="p-2 rounded-md bg-white/5 mb-2 overflow-hidden">
                          <code className="text-[11px] gch-text-muted font-mono whitespace-nowrap">&lt;iframe src="..." /&gt;</code>
                        </div>
                        <p className="text-[11px] gch-text-muted leading-relaxed">Drop the game into any web page with one snippet.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-4 space-y-4">
                  <div className="gch-card px-5 py-4">
                    <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary mb-3">Campaign</p>
                    {campaignMeta}
                    <div className="space-y-2">
                      <button onClick={handleOpenDuplicateModal} disabled={isDuplicating} className="w-full btn-admin-outline btn-admin-sm flex items-center gap-2 disabled:opacity-50">
                        <DocumentDuplicateIcon className="w-4 h-4" /> {isDuplicating ? 'Duplicating...' : 'Duplicate campaign'}
                      </button>
                    </div>
                  </div>
                  <details className="gch-card px-5 py-4">
                    <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-red-400/70 select-none">Danger zone</summary>
                    <div className="mt-3 space-y-2">
                      {effectiveCampaign?.options?.leaderboard?.enabled && leaderboardTotalEntries > 0 && (
                        <button type="button" onClick={handleOpenResetLeaderboardModal} disabled={isResettingLeaderboard} className="w-full btn-admin-outline btn-admin-sm flex items-center gap-2 text-red-400 border-red-400/30 hover:bg-red-500/10 disabled:opacity-50">
                          <TrashIcon className="w-4 h-4" /> {isResettingLeaderboard ? 'Resetting...' : 'Reset leaderboard'}
                        </button>
                      )}
                      <DeleteCampaign campaignId={effectiveCampaign.id} />
                    </div>
                  </details>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Main grid: Chart (8) + Sidebar (4) */}
              <div className="grid grid-cols-12 gap-6 mt-6">
                {/* Main column: chart + leaderboard */}
                <div className="col-span-12 lg:col-span-8 space-y-6">
                  {/* Mobile-only Share card - appears first on phones since sharing is the primary action */}
                  <div className="gch-card px-5 py-4 lg:hidden">
                    <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary mb-3">Share</p>
                    <CopyLinkButton
                      campaignId={effectiveCampaign.id}
                      slug={effectiveCampaign.slug}
                      utmSource="share"
                      onEditSlug={() => setIsSlugModalOpen(true)}
                    />
                    {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
                      <button
                        type="button"
                        onClick={handleNativeShare}
                        className="w-full btn-admin-primary btn-admin-md inline-flex items-center justify-center gap-2 mt-3"
                      >
                        <ShareIcon className="w-4 h-4" />
                        Share via…
                      </button>
                    )}
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      <button type="button" aria-label="Download QR code" onClick={() => downloadQR(`${window.location.origin}/p/${effectiveCampaign.slug || effectiveCampaign.id}?utm_source=qr`, `qr-code-${effectiveCampaign.slug || effectiveCampaign.id}.png`)} className="btn-admin-outline btn-admin-sm flex items-center justify-center gap-1.5">
                        <QrCodeIcon className="w-4 h-4 shrink-0" /><span className="text-xs">QR</span>
                      </button>
                      <button type="button" aria-label="Share on social media" onClick={() => setTimeout(() => setIsSocialModalOpen(true), 50)} className="btn-admin-outline btn-admin-sm flex items-center justify-center gap-1.5">
                        <ShareIcon className="w-4 h-4 shrink-0" /><span className="text-xs">Social</span>
                      </button>
                      <button type="button" aria-label="Get embed code" onClick={() => setTimeout(() => setIsEmbedModalOpen(true), 50)} className="btn-admin-outline btn-admin-sm flex items-center justify-center gap-1.5">
                        <CodeBracketIcon className="w-4 h-4 shrink-0" /><span className="text-xs">Embed</span>
                      </button>
                    </div>
                  </div>
                  {/* Key Metrics above chart */}
                  {derivedSidebarStats && derivedSidebarStats.totalPlays !== undefined && (() => {
                    const last7 = (campaignDailyStats || []).slice(-7);
                    const prev7 = (campaignDailyStats || []).slice(-14, -7);
                    const sparkFor = (key) => last7.map(d => Number(d?.[key]) || 0);
                    // Week-over-week delta: positive = up, negative = down,
                    // null = no comparison possible (fewer than 14 days of data).
                    const sumKey = (arr, key) => arr.reduce((acc, d) => acc + (Number(d?.[key]) || 0), 0);
                    const trendFor = (key) => {
                      if (prev7.length < 7) return null;
                      const cur = sumKey(last7, key);
                      const prev = sumKey(prev7, key);
                      if (cur === 0 && prev === 0) return null;
                      if (prev === 0) return { dir: 'up', pct: 100 };
                      const pct = Math.round(((cur - prev) / prev) * 100);
                      return { dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat', pct: Math.abs(pct) };
                    };
                    const renderTrend = (t) => {
                      if (!t) return null;
                      const arrow = t.dir === 'up' ? '↑' : t.dir === 'down' ? '↓' : '→';
                      const color = t.dir === 'up' ? 'text-emerald-400' : t.dir === 'down' ? 'text-red-400' : 'gch-text-muted';
                      return (
                        <span className={`text-[10px] font-semibold ${color} tabular-nums`} title="vs the 7 days before">
                          {arrow} {t.pct}%
                        </span>
                      );
                    };
                    // Surface useful conversion %s as the subtitle on each card.
                    const totalPlays = derivedSidebarStats.totalPlays || 0;
                    const totalCompletions = derivedSidebarStats.totalCompletions || 0;
                    const totalCtas = derivedSidebarStats.totalCtaClicks || 0;
                    const totalEmails = derivedSidebarStats.totalEmailCaptures || 0;
                    const scanToPlay = pageViewCount > 0 ? Math.round((totalPlays / pageViewCount) * 100) : null;
                    const completionRate = totalPlays > 0 ? Math.round((totalCompletions / totalPlays) * 100) : null;
                    const ctaRate = totalCompletions > 0 ? Math.round((totalCtas / totalCompletions) * 100) : null;
                    const emailRate = totalPlays > 0 ? Math.round((totalEmails / totalPlays) * 100) : null;
                    const renderSpark = (data, color) => {
                      if (!data.length || data.every(v => v === 0)) return null;
                      const w = 60, h = 18;
                      const max = Math.max(...data, 1);
                      const step = data.length > 1 ? w / (data.length - 1) : 0;
                      const pts = data.map((v, i) => `${i * step},${h - (v / max) * h}`).join(' ');
                      return (
                        <svg width={w} height={h} className="ml-auto shrink-0" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
                          <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={pts} opacity="0.85" />
                        </svg>
                      );
                    };
                    const metrics = [
                      {
                        label: 'Total Plays',
                        value: derivedSidebarStats.totalPlays,
                        icon: PlayIcon,
                        color: '#9B59B6',
                        spark: sparkFor('plays'),
                        // Scan/click → play conversion. Only shown once we
                        // have a meaningful number of page views to compare.
                        subtitle: scanToPlay !== null && pageViewCount >= 5
                          ? `${scanToPlay}% of ${pageViewCount.toLocaleString()} visits`
                          : null,
                        trend: trendFor('plays'),
                      },
                      {
                        label: 'Completions',
                        value: derivedSidebarStats.totalCompletions,
                        icon: CheckCircleIcon,
                        color: '#2ECC71',
                        spark: sparkFor('completions'),
                        subtitle: completionRate !== null ? `${completionRate}% finish rate` : null,
                        trend: trendFor('completions'),
                      },
                      {
                        label: 'CTA Clicks',
                        value: derivedSidebarStats.totalCtaClicks,
                        icon: CursorArrowRaysIcon,
                        color: '#3498DB',
                        spark: sparkFor('ctas'),
                        subtitle: ctaRate !== null ? `${ctaRate}% of finishers` : null,
                        trend: trendFor('ctas'),
                      },
                      {
                        label: 'Emails',
                        value: derivedSidebarStats.totalEmailCaptures,
                        icon: EnvelopeIcon,
                        color: '#F59E0B',
                        spark: sparkFor('emailCaptures'),
                        subtitle: emailRate !== null ? `${emailRate}% opt-in` : null,
                        trend: trendFor('emailCaptures'),
                      },
                    ];
                    const allZero = metrics.every(m => !m.value);
                    if (allZero) {
                      const isLive = campaignStatus === 'published' || campaignStatus === 'paused';
                      if (!isLive) {
                        return (
                          <div className="gch-card px-4 py-3 flex items-center gap-3 border border-white/15">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-white/5">
                              <PlayIcon className="w-4 h-4 gch-text-muted" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold gch-text-primary leading-tight">No plays yet</p>
                              <p className="text-xs gch-text-secondary leading-tight">Publish the campaign to start tracking plays, leads, and CTA clicks.</p>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div>
                          <div className="mb-3">
                            <h3 className="text-xs font-semibold uppercase tracking-wide gch-text-secondary">First plays</h3>
                            <p className="text-xs gch-text-muted mt-1">Four ways to get your first plays. Stats will appear here as soon as someone plays.</p>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {[
                              {
                                icon: ShareIcon,
                                title: 'Share the link',
                                body: 'Post directly to Instagram, WhatsApp, or anywhere you have an audience.',
                              },
                              {
                                icon: QrCodeIcon,
                                title: 'Print the QR',
                                body: 'Add it to a poster, beer mat, scoreboard, or table tent.',
                              },
                              {
                                icon: CodeBracketIcon,
                                title: 'Drop the embed',
                                body: 'Paste the embed snippet on your website or inside a marketing email.',
                              },
                              {
                                icon: ComputerDesktopIcon,
                                title: 'Run it on a screen',
                                body: 'Display at your next event. Players scan the QR to join on their phone.',
                              },
                            ].map(({ icon: Icon, title, body }) => (
                              <div key={title} className="gch-card px-4 py-3.5 border border-[var(--admin-border)] flex gap-3">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-[var(--admin-accent)]/10">
                                  <Icon className="w-4 h-4 text-[var(--admin-accent)]" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold gch-text-primary leading-tight mb-0.5">{title}</p>
                                  <p className="text-xs gch-text-secondary leading-snug">{body}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {metrics.map(({ label, value, icon: Icon, color, subtitle, spark, trend }) => (
                          <div key={label} className="gch-card px-4 py-3.5 border border-white/15">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}20` }}>
                                <Icon className="w-4 h-4" style={{ color }} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-xl font-bold tabular-nums gch-text-primary leading-none">
                                    {typeof value === 'number' ? formatCompact(value) : value}
                                  </p>
                                  {renderSpark(spark, color)}
                                </div>
                                <div className="flex items-center gap-1.5 mt-1">
                                  <p className="text-[10px] font-medium uppercase tracking-wider gch-text-muted truncate" title={label}>{label}</p>
                                  {renderTrend(trend)}
                                </div>
                                {subtitle && <p className="text-[10px] font-medium gch-text-secondary mt-0.5">{subtitle}</p>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {/* Hide the Recent Activity chart card entirely when there are no plays -
                      the compact "No plays yet" pill above already conveys empty state. */}
                  {(derivedSidebarStats?.totalPlays || 0) > 0 && (
                    <Stats
                      campaignIds={[effectiveCampaign.id]}
                      campaign={effectiveCampaign}
                      hideStatCards
                      prefetchedStats={campaignStats}
                      prefetchedDailyStats={campaignDailyStats}
                      lastUpdated={statsUpdatedAt}
                      onRefresh={handleStatsForceRefresh}
                    />
                  )}

                  {/* Insights (Devices + Traffic Sources) - only shown when at least one tab has data */}
                  {derivedSidebarStats && (() => {
                    const devices = derivedSidebarStats.devices || {};
                    const totalDevices = (devices.mobile || 0) + (devices.desktop || 0) + (devices.tablet || 0);
                    const devicePct = (count) => totalDevices > 0 ? Math.round((count / totalDevices) * 100) : 0;
                    const isSelfReferrer = (r) => /^(direct|localhost|.*smilesflow\..*)$/i.test(r);
                    const filteredReferrers = (derivedSidebarStats.topReferrers || []).filter((r) => !isSelfReferrer(r.name));
                    const referrerTotal = filteredReferrers.reduce((s, r) => s + (r.count || 0), 0);
                    const utmTotal = (derivedSidebarStats.topUtmSources || []).reduce((s, u) => s + (u.count || 0), 0);
                    const hasTraffic = (filteredReferrers.length > 0) || (derivedSidebarStats.topUtmSources?.length > 0);
                    const hasEngagement = (derivedSidebarStats.totalPlays || 0) > 0;
                    const hasAnyInsights = totalDevices > 0 || hasTraffic || hasEngagement;
                    if (!hasAnyInsights) return null;
                    return (
                      <div className="gch-card overflow-hidden">
                        <header className="px-5 py-4 border-b border-[var(--admin-border)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <h2 className="text-xs font-semibold uppercase tracking-wide gch-text-secondary">Insights</h2>
                          <div className="flex rounded-lg p-1 gch-bg-card border border-[var(--admin-border)] self-start sm:self-auto">
                            <button
                              onClick={() => setInsightsTab('devices')}
                              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${insightsTab === 'devices' ? 'bg-[var(--admin-cta-solid)] text-white' : 'gch-text-secondary hover:bg-white/10'}`}
                            >Devices</button>
                            <button
                              onClick={() => setInsightsTab('sources')}
                              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${insightsTab === 'sources' ? 'bg-[var(--admin-cta-solid)] text-white' : 'gch-text-secondary hover:bg-white/10'}`}
                            >Traffic Sources</button>
                            <button
                              onClick={() => setInsightsTab('engagement')}
                              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${insightsTab === 'engagement' ? 'bg-[var(--admin-cta-solid)] text-white' : 'gch-text-secondary hover:bg-white/10'}`}
                            >Engagement</button>
                          </div>
                        </header>
                        <div className="px-5 py-4">
                          {insightsTab === 'devices' && totalDevices > 0 && (
                            <div className="space-y-3">
                              {[
                                { name: 'Mobile', count: devices.mobile || 0, icon: DevicePhoneMobileIcon },
                                { name: 'Desktop', count: devices.desktop || 0, icon: ComputerDesktopIcon },
                                { name: 'Tablet', count: devices.tablet || 0, icon: DeviceTabletIcon },
                              ].map(({ name, count, icon: Icon }) => {
                                const pct = devicePct(count);
                                return (
                                  <div key={name}>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="flex items-center gap-2 text-xs gch-text-secondary">
                                        <Icon className="w-3.5 h-3.5" />
                                        {name}
                                      </span>
                                      <span className="text-xs font-semibold tabular-nums gch-text-primary">
                                        {count.toLocaleString()} ({pct}%)
                                      </span>
                                    </div>
                                    <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: 'var(--admin-accent)' }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {insightsTab === 'devices' && totalDevices === 0 && (
                            <p className="text-xs gch-text-muted">No device data yet.</p>
                          )}
                          {insightsTab === 'sources' && (
                            <div className="space-y-4">
                              {!hasTraffic && (
                                <p className="text-xs gch-text-muted">No traffic source data yet.</p>
                              )}
                              {filteredReferrers.length > 0 && (
                                <div>
                                  <p className="text-[10px] font-medium uppercase tracking-wider gch-text-muted mb-2">Referrers</p>
                                  <div className="space-y-1.5">
                                    {filteredReferrers.map(({ name, count }) => {
                                      const pct = referrerTotal > 0 ? Math.round((count / referrerTotal) * 100) : 0;
                                      return (
                                        <div key={name} className="flex items-center justify-between gap-3">
                                          <span className="text-xs gch-text-secondary truncate">{name}</span>
                                          <span className="text-xs font-semibold tabular-nums gch-text-primary shrink-0">
                                            {count.toLocaleString()} ({pct}%)
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {derivedSidebarStats.topUtmSources?.length > 0 && (
                                <div className={filteredReferrers.length > 0 ? 'pt-3 border-t border-white/5' : ''}>
                                  <p className="text-[10px] font-medium uppercase tracking-wider gch-text-muted mb-2">UTM Sources</p>
                                  <div className="space-y-1.5">
                                    {derivedSidebarStats.topUtmSources.map(({ name, count }) => {
                                      const pct = utmTotal > 0 ? Math.round((count / utmTotal) * 100) : 0;
                                      return (
                                        <div key={name} className="flex items-center justify-between gap-3">
                                          <span className="text-xs gch-text-secondary truncate">{name}</span>
                                          <span className="text-xs font-semibold tabular-nums gch-text-primary shrink-0">
                                            {count.toLocaleString()} ({pct}%)
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              <p className="text-[10px] gch-text-muted pt-2 border-t border-white/5">
                                Tip: Add <code className="px-1 py-0.5 rounded bg-white/5 text-[10px]">?utm_source=instagram</code> to your game link to track where players come from.
                              </p>
                            </div>
                          )}
                          {insightsTab === 'engagement' && (
                            <div className="space-y-5">
                              <div>
                                <p className="text-[10px] font-medium uppercase tracking-wider gch-text-muted mb-3">Rates</p>
                                <div className="space-y-4">
                                  {[
                                    { label: 'Completion Rate', value: derivedSidebarStats.completionRate || 0, color: '#2ECC71', sub: 'of plays' },
                                    { label: 'Share Rate', value: derivedSidebarStats.shareRate || 0, color: '#F59E0B', sub: 'of completions' },
                                    { label: 'CTA Click Rate', value: derivedSidebarStats.ctaRate || 0, color: '#3498DB', sub: 'of completions' },
                                  ].map(({ label, value, color, sub }) => (
                                    <div key={label}>
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs font-medium gch-text-secondary">
                                          {label}
                                          {sub && <span className="gch-text-muted ml-1 font-normal">({sub})</span>}
                                        </span>
                                        <span className="text-sm font-bold tabular-nums" style={{ color }}>
                                          {value}%
                                        </span>
                                      </div>
                                      <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                                        <div
                                          className="h-full rounded-full transition-all duration-700 ease-out"
                                          style={{
                                            width: `${Math.max(value, 0)}%`,
                                            backgroundColor: color,
                                            opacity: 0.85,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="pt-3 border-t border-white/5">
                                <p className="text-[10px] font-medium uppercase tracking-wider gch-text-muted mb-2">Details</p>
                                <div className="space-y-2.5">
                                  {[
                                    { name: 'Shares', value: (derivedSidebarStats.totalShares || 0).toLocaleString(), icon: ShareIcon },
                                    { name: 'Avg Duration', value: derivedSidebarStats.avgDurationSeconds > 0 ? `${derivedSidebarStats.avgDurationSeconds}s` : '-', icon: ClockIcon },
                                  ].map(({ name, value, icon: Icon }) => (
                                    <div key={name} className="flex items-center justify-between">
                                      <span className="flex items-center gap-2 text-xs gch-text-secondary">
                                        <Icon className="w-3.5 h-3.5" />
                                        {name}
                                      </span>
                                      <span className="text-sm font-semibold tabular-nums gch-text-primary">{value}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {effectiveCampaign?.options?.leaderboard?.enabled && (
                    <ManageLeaderboard key={`lb-${effectiveCampaign?.id}-${leaderboardResetKey}`} campaignId={effectiveCampaign.id} campaign={effectiveCampaign} onLoadTotals={handleLeaderboardTotals} />
                  )}

                </div>

                {/* Sidebar */}
                <div className="col-span-12 lg:col-span-4 space-y-4">
                  {/* Quick actions  - always visible at top */}
                  {campaignStatus !== 'draft' && (
                    <div className="gch-card px-5 py-4">
                      <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary mb-3">Quick actions</p>
                      <div className="grid grid-cols-1 gap-2">
                        <Link
                          to={`/editor/${effectiveCampaign.id}`}
                          className="w-full btn-admin-primary btn-admin-md inline-flex items-center justify-center gap-2"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                          Open editor
                        </Link>
                        {campaignStatus !== 'paused' && (
                          <a
                            href={`/p/${effectiveCampaign.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full btn-admin-outline btn-admin-md inline-flex items-center justify-center gap-2"
                          >
                            <PlayIcon className="w-4 h-4" />
                            Play
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Share  - rendered desktop-only here; mobile version is at top of main column */}
                  <div className="gch-card px-5 py-4 hidden lg:block">
                    <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary mb-3">Share</p>
                    <CopyLinkButton
                      campaignId={effectiveCampaign.id}
                      slug={effectiveCampaign.slug}
                      utmSource="share"
                      onEditSlug={() => setIsSlugModalOpen(true)}
                    />
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      <button type="button" title="Download QR code" aria-label="Download QR code" onClick={() => downloadQR(`${window.location.origin}/p/${effectiveCampaign.slug || effectiveCampaign.id}?utm_source=qr`, `qr-code-${effectiveCampaign.slug || effectiveCampaign.id}.png`)} className="btn-admin-outline btn-admin-sm flex items-center justify-center gap-1.5">
                        <QrCodeIcon className="w-4 h-4 shrink-0" /><span className="text-xs">QR</span>
                      </button>
                      <button type="button" title="Share on social media" aria-label="Share on social media" onClick={() => setTimeout(() => setIsSocialModalOpen(true), 50)} className="btn-admin-outline btn-admin-sm flex items-center justify-center gap-1.5">
                        <ShareIcon className="w-4 h-4 shrink-0" /><span className="text-xs">Social</span>
                      </button>
                      <button type="button" title="Get embed code" aria-label="Get embed code" onClick={() => setTimeout(() => setIsEmbedModalOpen(true), 50)} className="btn-admin-outline btn-admin-sm flex items-center justify-center gap-1.5">
                        <CodeBracketIcon className="w-4 h-4 shrink-0" /><span className="text-xs">Embed</span>
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={handleShareResults}
                      disabled={isGeneratingPassword}
                      className="w-full btn-admin-outline btn-admin-sm flex items-center gap-2 mt-2 disabled:opacity-50"
                    >
                      <ChartBarIcon className="w-4 h-4 shrink-0" /><span>{isGeneratingPassword ? 'Generating...' : 'Share report'}</span>
                    </button>
                  </div>
                  <SlugEditor
                    campaignId={effectiveCampaign.id}
                    currentSlug={effectiveCampaign.slug || ''}
                    suggestionSource={slugSuggestionSource}
                    modalOpen={isSlugModalOpen}
                    setModalOpen={setIsSlugModalOpen}
                    onSaved={(newSlug) => setCampaign((prev) => ({ ...(prev || effectiveCampaign), slug: newSlug }))}
                  />

                  <div className="gch-card px-5 py-4">
                    <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary mb-3">Campaign</p>
                    {campaignMeta}
                    <div className="space-y-2">
                      <button onClick={handleOpenDuplicateModal} disabled={isDuplicating} className="w-full btn-admin-outline btn-admin-sm flex items-center gap-2 disabled:opacity-50">
                        <DocumentDuplicateIcon className="w-4 h-4" /> {isDuplicating ? 'Duplicating...' : 'Duplicate campaign'}
                      </button>
                      <button onClick={handleExportReport} disabled={isExporting} className="w-full btn-admin-outline btn-admin-sm flex items-center gap-2 disabled:opacity-50">
                        <ArrowDownTrayIcon className="w-4 h-4" /> {isExporting ? 'Exporting...' : 'Export report'}
                      </button>
                    </div>
                  </div>

                  {/* Admin Tools - only visible to admins */}
                  {isAdmin && (
                    <div className="gch-card px-5 py-4 ring-1 ring-amber-500/30">
                      <p className="text-xs font-semibold uppercase tracking-wide text-amber-400 mb-3 flex items-center gap-1.5">
                        <ShieldCheckIcon className="w-3.5 h-3.5" /> Admin Tools
                      </p>
                      <div className="space-y-2">
                        {/* Template Library */}
                        {templateData ? (
                          <button
                            type="button"
                            onClick={handleRemoveTemplate}
                            disabled={removingTemplate}
                            className="w-full btn-admin-outline btn-admin-sm flex items-center gap-2 text-amber-400 border-amber-400/30 hover:bg-amber-500/10 disabled:opacity-50"
                          >
                            {removingTemplate ? 'Removing...' : 'Remove from templates'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setTemplateForm({
                                name: effectiveCampaign.internalName || effectiveCampaign.options?.title || effectiveCampaign.name || '',
                                description: '',
                                category: 'marketing',
                                useCase: '',
                                imageUrl: '',
                              });
                              setTemplateImageFile(null);
                              setTimeout(() => setShowTemplateModal(true), 50);
                            }}
                            className="w-full btn-admin-outline btn-admin-sm flex items-center gap-2"
                          >
                            Add to template library
                          </button>
                        )}

                        {/* Force Actions */}
                        {campaignStatus === 'published' && (
                          <button
                            type="button"
                            onClick={() => handleAdminAction('unpublish')}
                            disabled={!!adminActionLoading}
                            className="w-full btn-admin-outline btn-admin-sm flex items-center gap-2 text-amber-400 border-amber-400/30 hover:bg-amber-500/10 disabled:opacity-50"
                          >
                            {adminActionLoading === 'unpublish' ? 'Unpublishing...' : 'Force unpublish'}
                          </button>
                        )}
                        {(campaignStatus === 'published' || campaignStatus === 'paused') && (
                          <button
                            type="button"
                            onClick={() => handleAdminAction('complete')}
                            disabled={!!adminActionLoading}
                            className="w-full btn-admin-outline btn-admin-sm flex items-center gap-2 text-amber-400 border-amber-400/30 hover:bg-amber-500/10 disabled:opacity-50"
                          >
                            {adminActionLoading === 'complete' ? 'Completing...' : 'Force complete'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleAdminAction('refundCredit')}
                          disabled={!!adminActionLoading}
                          className="w-full btn-admin-outline btn-admin-sm flex items-center gap-2 disabled:opacity-50"
                        >
                          {adminActionLoading === 'refundCredit' ? 'Refunding...' : 'Refund publish credit'}
                        </button>
                      </div>
                    </div>
                  )}

                  <details className="gch-card px-5 py-4">
                    <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-red-400/70 select-none">Danger zone</summary>
                    <div className="mt-3 space-y-2">
                      {(campaignStatus === 'published' || campaignStatus === 'paused' || campaignStatus === 'completed') && (
                        <button onClick={() => setShowRevertModal(true)} className="w-full btn-admin-outline btn-admin-sm flex items-center gap-2 text-red-400 border-red-400/30 hover:bg-red-500/10">
                          <ExclamationTriangleIcon className="w-4 h-4" /> {campaignStatus === 'completed' ? 'Restore to Draft' : 'Revert to Draft'}
                        </button>
                      )}
                      {effectiveCampaign?.options?.leaderboard?.enabled && leaderboardTotalEntries > 0 && (
                        <button type="button" onClick={handleOpenResetLeaderboardModal} disabled={isResettingLeaderboard} className="w-full btn-admin-outline btn-admin-sm flex items-center gap-2 text-red-400 border-red-400/30 hover:bg-red-500/10 disabled:opacity-50">
                          <TrashIcon className="w-4 h-4" /> {isResettingLeaderboard ? 'Resetting...' : 'Reset leaderboard'}
                        </button>
                      )}
                      <DeleteCampaign campaignId={effectiveCampaign.id} />
                    </div>
                  </details>
                </div>
              </div>

            </>
          )}
      </motion.div>
    </AnimatePresence>
      </div>

{/* Social Share Modal - always mounted so Transition works like CampaignEmbed */}
    <ModalBasic
      id="social-share-modal"
      modalOpen={isSocialModalOpen}
      setModalOpen={setIsSocialModalOpen}
      title="Social share"
    >
      <div className="p-6">
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold gch-text-secondary uppercase tracking-wide mb-2">Preview</p>
            <div className="rounded-xl border border-[var(--admin-border)] overflow-hidden gch-input-ghost">
              <div className="aspect-[1.91/1] bg-black/20 relative">
                <img
                  src={shareImage}
                  alt="Share preview"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-3 border-t border-[var(--admin-border)]">
                <p className="text-xs gch-text-muted uppercase tracking-wide mb-1">smilesflow.com</p>
                <p className="text-sm font-semibold gch-text-primary line-clamp-2">{shareTitle}</p>
              </div>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold gch-text-secondary uppercase tracking-wide mb-2">Copy link for</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Facebook', utm: 'facebook' },
                { label: 'Instagram', utm: 'instagram' },
                { label: 'X / Twitter', utm: 'twitter' },
                { label: 'LinkedIn', utm: 'linkedin' },
                { label: 'TikTok', utm: 'tiktok' },
                { label: 'Email', utm: 'email' },
              ].map(({ label, utm }) => (
                <button
                  key={utm}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(`${window.location.origin}/p/${effectiveCampaign?.id ?? ''}`);
                      setSocialCopied(utm);
                      setTimeout(() => setSocialCopied(null), 2000);
                    } catch (e) { console.error(e); }
                  }}
                  className="btn-admin-outline btn-admin-sm text-xs"
                >
                  {socialCopied === utm ? 'Copied!' : label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs gch-text-muted">Each link is tagged so you can see which platform drives the most plays.</p>
        </div>
      </div>
    </ModalBasic>

    {/* Embed Code Modal - always mounted so Transition works like CampaignEmbed */}
    <ModalBasic
      id="embed-code-modal"
      modalOpen={isEmbedModalOpen}
      setModalOpen={setIsEmbedModalOpen}
      title="Embed code"
    >
      <div className="p-6">
        <div className="space-y-4">
          <p className="text-sm font-semibold gch-text-secondary">Add this code to your website:</p>
          <div className="p-4 rounded-lg border border-[var(--admin-border)] gch-input-ghost">
            <pre className="text-sm whitespace-pre-wrap break-all gch-text-primary" style={{ fontFamily: '"JetBrains Mono", monospace', lineHeight: '1.4' }}>
              {embedCode}
            </pre>
          </div>
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(embedCode);
                setEmbedCopied(true);
                setTimeout(() => setEmbedCopied(false), 2000);
              } catch (e) { console.error(e); }
            }}
            className="w-full btn-admin-secondary btn-admin-md inline-flex items-center justify-center"
          >
            {embedCopied ? 'Code Copied!' : 'Copy Code'}
          </button>
        </div>
      </div>
    </ModalBasic>

    {/* Publish confirmation modal for the inline nudge button. */}
    <ModalBasic
      id="publish-confirm-modal"
      modalOpen={showPublishConfirm}
      setModalOpen={setShowPublishConfirm}
      title="Publish your game?"
    >
      <div className="p-6 space-y-5">
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
          <p className="text-sm font-semibold text-emerald-300">Free publish</p>
          <p className="text-xs text-emerald-200/80 mt-0.5">This will use your free slot. No charge.</p>
        </div>
        <p className="text-sm gch-text-secondary leading-relaxed">
          Your game will go live with a public link, QR code, and leaderboard. You can pause or edit it anytime.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setShowPublishConfirm(false)}
            disabled={isPublishingFromNudge}
            className="flex-1 btn-admin-outline btn-admin-md disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => {
              setIsPublishingFromNudge(true);
              setShowPublishConfirm(false);
              try {
                await handleUpdateCampaignStatus('published');
              } finally {
                setIsPublishingFromNudge(false);
              }
            }}
            disabled={isPublishingFromNudge}
            className="flex-1 btn-admin-primary btn-admin-md inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isPublishingFromNudge ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                </svg>
                Publishing…
              </>
            ) : 'Publish'}
          </button>
        </div>
      </div>
    </ModalBasic>

    {/* First-publish celebration modal: fires once per user. Bundles
        everything the user needs to actually share their freshly-live
        game (link, QR, embed, distribution ideas) so the post-publish
        moment doesn't end with a toast that disappears in 4 seconds. */}
    <ModalBasic
      id="first-publish-modal"
      modalOpen={showFirstPublishModal}
      setModalOpen={(open) => {
        if (!open) {
          try { localStorage.setItem('sf_first_publish_seen', '1'); } catch {}
        }
        setShowFirstPublishModal(open);
      }}
      title="🎉 Your game is live"
    >
      <div className="p-6 space-y-4">
        {/* Live link */}
        <div className="flex items-center gap-2 min-w-0">
          <div className="min-w-0 flex-1 p-3 rounded-lg border border-[var(--admin-border)] gch-input-ghost">
            <p className="text-xs gch-text-primary truncate font-mono">
              {`${window.location.origin}/p/${effectiveCampaign?.slug || effectiveCampaign?.id || ''}`}
            </p>
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(`${window.location.origin}/p/${effectiveCampaign?.slug || effectiveCampaign?.id || ''}?utm_source=share`);
                setFirstPublishLinkCopied(true);
                setTimeout(() => setFirstPublishLinkCopied(false), 2000);
              } catch (e) { console.error(e); }
            }}
            className="btn-admin-primary btn-admin-md shrink-0"
          >
            {firstPublishLinkCopied ? 'Copied' : 'Copy link'}
          </button>
        </div>

        {/* Secondary actions: QR download + embed copy, equal weight */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => downloadQR(
              `${window.location.origin}/p/${effectiveCampaign?.slug || effectiveCampaign?.id || ''}?utm_source=qr`,
              `qr-code-${effectiveCampaign?.slug || effectiveCampaign?.id || 'game'}.png`
            )}
            className="btn-admin-outline btn-admin-md inline-flex items-center justify-center gap-2"
          >
            <QrCodeIcon className="w-4 h-4" />
            Download QR
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(embedCode);
                setFirstPublishEmbedCopied(true);
                setTimeout(() => setFirstPublishEmbedCopied(false), 2000);
              } catch (e) { console.error(e); }
            }}
            className="btn-admin-outline btn-admin-md inline-flex items-center justify-center gap-2"
          >
            <CodeBracketIcon className="w-4 h-4" />
            {firstPublishEmbedCopied ? 'Copied' : 'Copy embed'}
          </button>
        </div>

        <p className="text-[11px] gch-text-muted text-center">All of this is on the campaign page anytime.</p>

        <button
          type="button"
          onClick={() => {
            try { localStorage.setItem('sf_first_publish_seen', '1'); } catch {}
            setShowFirstPublishModal(false);
          }}
          className="w-full btn-admin-primary btn-admin-md"
        >
          Done
        </button>
      </div>
    </ModalBasic>

    {/* Results Share Modal */}
    <ModalBasic
      id="results-share-modal"
      modalOpen={isResultsShareModalOpen}
      setModalOpen={setIsResultsShareModalOpen}
      title="Share report"
    >
      <div className="p-6">
        <div className="space-y-4">
          <p className="text-xs gch-text-secondary">Anyone with this link and password can view live campaign results.</p>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary mb-2">Results page link</p>
            <div className="flex items-center gap-2 min-w-0">
              <div className="min-w-0 flex-1 p-3 rounded-lg border border-[var(--admin-border)] gch-input-ghost overflow-hidden">
                <p className="text-xs gch-text-primary truncate">
                  {effectiveCampaign?.id ? `${window.location.origin}/results/${effectiveCampaign.id}` : ''}
                </p>
              </div>
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(`${window.location.origin}/results/${effectiveCampaign.id}`);
                    setResultsUrlCopied(true);
                    setTimeout(() => setResultsUrlCopied(false), 2000);
                  } catch (e) { console.error(e); }
                }}
                className="btn-admin-secondary btn-admin-sm shrink-0"
              >
                {resultsUrlCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {resultsPlainPassword && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary mb-2">Password</p>
              <div className="flex items-center gap-2 min-w-0">
                <div className="min-w-0 flex-1 p-3 rounded-lg border border-[var(--admin-border)] gch-input-ghost overflow-hidden">
                  <p className="text-sm font-bold gch-text-primary tracking-widest" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
                    {resultsPlainPassword}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(resultsPlainPassword);
                      setResultsPasswordCopied(true);
                      setTimeout(() => setResultsPasswordCopied(false), 2000);
                    } catch (e) { console.error(e); }
                  }}
                  className="btn-admin-secondary btn-admin-sm shrink-0"
                >
                  {resultsPasswordCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-xs gch-text-muted mt-2">Save this password - it won't be shown again.</p>
            </div>
          )}

          {!resultsPlainPassword && effectiveCampaign?.resultsPasswordHash && (
            <div className="p-3 rounded-lg border border-[var(--admin-border)] gch-input-ghost">
              <p className="text-xs gch-text-secondary">This page is password protected. If you've lost the password, regenerate a new one below.</p>
            </div>
          )}

          <button
            onClick={handleRegenerateResultsPassword}
            disabled={isGeneratingPassword}
            className="w-full btn-admin-outline btn-admin-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isGeneratingPassword ? 'Generating...' : resultsPlainPassword ? 'Regenerate password' : effectiveCampaign?.resultsPasswordHash ? 'Regenerate password' : 'Generate password'}
          </button>

          <a
            href={`${window.location.origin}/results/${effectiveCampaign?.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full btn-admin-secondary btn-admin-md inline-flex items-center justify-center gap-2"
          >
            Open results page <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </ModalBasic>

    {/* Revert to Draft Modal */}
    {showRevertModal && (
      <ModalBasic
        id="revert-campaign-modal"
        modalOpen={showRevertModal}
        setModalOpen={setShowRevertModal}
        title={campaignStatus === 'completed' ? 'Restore to draft?' : 'Revert to draft?'}
      >
        <div className="p-6">
          <p className="text-sm gch-text-secondary mb-4">
            {campaignStatus === 'published'
              ? 'The game link will stop working immediately. Players will no longer be able to play. You\'ll need to publish again to go live.'
              : campaignStatus === 'paused'
              ? 'The campaign will move back to draft. You\'ll need to publish again to go live.'
              : 'The campaign will move back to draft. You\'ll need to publish again to go live.'}
          </p>
          <div className="rounded-xl p-4 mb-6 space-y-2 border border-amber-500/20 bg-amber-500/5">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm gch-text-secondary">
                {campaignStatus === 'published'
                  ? 'Game link and QR code will stop working'
                  : 'You\'ll need to republish to go live again'}
              </span>
            </div>
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm gch-text-secondary">All data and leaderboard preserved</span>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowRevertModal(false)} className="btn-admin-outline btn-admin-md">Cancel</button>
            <button onClick={handleRevertConfirm} disabled={isReverting} className="btn-admin-secondary btn-admin-md disabled:opacity-50">
              {isReverting ? 'Updating...' : (campaignStatus === 'completed' ? 'Restore to Draft' : 'Revert to Draft')}
            </button>
          </div>
        </div>
      </ModalBasic>
    )}

    {/* Duplicate Game Modal */}
    {showDuplicateModal && (
      <ModalBasic
        id="duplicate-game-modal"
        modalOpen={duplicateModalOpen}
        setModalOpen={handleCloseDuplicateModal}
        title="Duplicate campaign"
      >
        <div className="p-6">
          <div className="mb-6">
            <p className="text-sm gch-text-secondary mb-4">
              This will create a complete copy of <strong className="gch-text-primary">{effectiveCampaign?.options?.title || 'this campaign'}</strong> with:
            </p>
            <ul className="text-sm gch-text-secondary text-left space-y-2 gch-input-ghost rounded-lg p-4 border border-[var(--admin-border)]">
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                New unique campaign ID and QR code
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                Fresh leaderboard (no existing scores)
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                All campaign settings and appearance copied
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                Stats start from zero
              </li>
            </ul>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={handleCloseDuplicateModal}
              className="btn-admin-outline btn-admin-md"
            >
              Cancel
            </button>
            <button
              onClick={handleDuplicate}
              disabled={isDuplicating}
              className="btn-admin-secondary btn-admin-md disabled:opacity-50"
            >
              {isDuplicating ? 'Duplicating...' : 'Duplicate campaign'}
            </button>
          </div>
        </div>
      </ModalBasic>
    )}

    {/* Reset Leaderboard Modal */}
    {showResetLeaderboardModal && (
      <ModalBasic
        id="reset-leaderboard-modal"
        modalOpen={resetLeaderboardModalOpen}
        setModalOpen={handleCloseResetLeaderboardModal}
        title="Reset Leaderboard"
      >
        <div className="p-6">
          <div className="mb-6">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-red-500/20">
                <ExclamationTriangleIcon className="w-10 h-10 text-red-400" />
              </div>
            </div>
            <h3 className="text-lg font-semibold gch-text-primary text-center mb-2">
              This will permanently delete all leaderboard data
            </h3>
            <div className="text-sm gch-text-secondary mb-4 space-y-2 text-center">
              <p>You are about to delete:</p>
              <ul className="text-left bg-red-500/10 rounded-lg p-3 border border-red-400/30 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="text-red-400">•</span>
                  <strong className="gch-text-primary">{leaderboardTotalEntries.toLocaleString()}</strong> leads
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-400">•</span>
                  <strong className="gch-text-primary">{leaderboardEmailCount.toLocaleString()}</strong> captured emails
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-400">•</span>
                  All scores and rankings
                </li>
              </ul>
              <p className="text-red-400 font-medium mt-3">
                This action cannot be undone!
              </p>
            </div>
            <div className="text-left mb-4">
              <label className="block text-sm font-semibold mb-2 gch-text-primary">
                Type <span className="font-mono gch-input-ghost px-1.5 py-0.5 rounded text-red-400">{RESET_LEADERBOARD_CONFIRM_WORD}</span> to confirm
              </label>
              <input
                type="text"
                value={resetLeaderboardConfirmText}
                onChange={(e) => setResetLeaderboardConfirmText(e.target.value.toUpperCase())}
                placeholder={RESET_LEADERBOARD_CONFIRM_WORD}
                className="w-full px-3 py-2 rounded-lg font-mono text-center text-lg gch-input-ghost gch-text-primary border border-[var(--admin-border)] focus:border-red-400 focus:ring-2 focus:ring-red-500/20"
                autoFocus
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={handleCloseResetLeaderboardModal}
              className="btn-admin-outline btn-admin-md"
            >
              Cancel
            </button>
            <button
              onClick={handleResetLeaderboard}
              disabled={resetLeaderboardConfirmText !== RESET_LEADERBOARD_CONFIRM_WORD || isResettingLeaderboard}
              className={`px-4 py-2.5 rounded-[var(--admin-card-radius)] font-semibold text-sm transition-all ${
                resetLeaderboardConfirmText === RESET_LEADERBOARD_CONFIRM_WORD && !isResettingLeaderboard
                  ? 'bg-red-600 hover:bg-red-500 text-white'
                  : 'gch-input-ghost gch-text-muted cursor-not-allowed'
              }`}
            >
              {isResettingLeaderboard ? 'Resetting...' : 'Reset Leaderboard'}
            </button>
          </div>
        </div>
      </ModalBasic>
    )}

    {/* Admin: Add to Template Library Modal */}
    {showTemplateModal && (
      <ModalBasic
        id="admin-template-modal"
        modalOpen={showTemplateModal}
        setModalOpen={setShowTemplateModal}
        title="Add to Template Library"
      >
        <div className="p-6 space-y-5">
          <div>
            <label className="text-xs font-medium gch-text-secondary block mb-1.5">Template Name <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={templateForm.name}
              onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-[var(--admin-card-radius)] gch-input-ghost border border-[var(--admin-border)] gch-text-primary placeholder:gch-text-muted text-sm focus:ring-2 focus:ring-[var(--admin-accent)] focus:outline-none"
              placeholder="e.g. Summer Promo Runner"
            />
          </div>
          <div>
            <label className="text-xs font-medium gch-text-secondary block mb-1.5">Description</label>
            <input
              type="text"
              value={templateForm.description}
              onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-[var(--admin-card-radius)] gch-input-ghost border border-[var(--admin-border)] gch-text-primary placeholder:gch-text-muted text-sm focus:ring-2 focus:ring-[var(--admin-accent)] focus:outline-none"
              placeholder="Short description for the gallery"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium gch-text-secondary block mb-1.5">Category</label>
              <select
                value={templateForm.category}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-[var(--admin-card-radius)] gch-input-ghost border border-[var(--admin-border)] gch-text-primary text-sm focus:ring-2 focus:ring-[var(--admin-accent)] focus:outline-none cursor-pointer"
              >
                <option value="marketing">Marketing</option>
                <option value="events">Events</option>
                <option value="agency">Agency</option>
                <option value="seasonal">Seasonal</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium gch-text-secondary block mb-1.5">Use Case</label>
              <input
                type="text"
                value={templateForm.useCase}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, useCase: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-[var(--admin-card-radius)] gch-input-ghost border border-[var(--admin-border)] gch-text-primary placeholder:gch-text-muted text-sm focus:ring-2 focus:ring-[var(--admin-accent)] focus:outline-none"
                placeholder="e.g. Product launch"
              />
            </div>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={templateForm.showOnLanding}
              onChange={(e) => setTemplateForm(prev => ({ ...prev, showOnLanding: e.target.checked }))}
              className="w-4 h-4 rounded border-[var(--admin-border)] bg-white/5 text-[var(--admin-accent)] focus:ring-[var(--admin-accent)]"
            />
            <span className="text-sm gch-text-primary">Show on landing page</span>
          </label>
          <div>
            <label className="text-xs font-medium gch-text-secondary block mb-1.5">Template Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setTemplateImageFile(e.target.files?.[0] || null)}
              className="w-full text-sm gch-text-secondary file:mr-3 file:py-2 file:px-4 file:rounded-[var(--admin-card-radius)] file:border file:border-[var(--admin-border)] file:bg-white/5 file:text-white file:text-xs file:font-medium hover:file:bg-white/10 file:cursor-pointer file:transition-colors"
            />
            {templateForm.imageUrl && !templateImageFile && (
              <p className="text-xs gch-text-muted mt-1.5">Current: {templateForm.imageUrl.substring(0, 40)}...</p>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-3 border-t border-[var(--admin-border)]">
            <button
              type="button"
              onClick={() => setShowTemplateModal(false)}
              className="btn-admin-outline btn-admin-md"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddTemplate}
              disabled={savingTemplate || !templateForm.name.trim()}
              className="btn-admin-primary btn-admin-md disabled:opacity-50"
            >
              {savingTemplate ? 'Adding...' : 'Add to Library'}
            </button>
          </div>
        </div>
      </ModalBasic>
    )}

  </>
  );
}
